// Texting rules shared by the server and the screens: store hours, the STOP / START / HELP
// keywords carriers require, message templates and SMS length.

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Minutes after midnight, open to close, for each weekday (0 = Sunday). An empty list is closed. */
export type WeekHours = { open: number; close: number }[][];

export type HoursResult = { ok: true; week: WeekHours } | { ok: false; error: string };

function dayIndex(word: string): number {
  const w = word.trim().slice(0, 3).toLowerCase();
  return WEEKDAYS.findIndex((d) => d.toLowerCase() === w);
}

/** "10", "10:30", "10am", "7:30pm", "19:00" → minutes after midnight. */
function parseTime(t: string): number | null {
  const m = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm|a|p)?$/i.exec(t.trim());
  if (!m) return null;
  let h = Number(m[1]);
  const min = Number(m[2] ?? 0);
  const ap = m[3]?.toLowerCase();
  if (min > 59) return null;
  if (ap) {
    if (h < 1 || h > 12) return null;
    if (ap.startsWith('p') && h !== 12) h += 12;
    if (ap.startsWith('a') && h === 12) h = 0;
  }
  if (h > 24 || (h === 24 && min > 0)) return null;
  return h * 60 + min;
}

/**
 * Reads store hours, one line per day or range of days:
 *   Mon-Sat 10:00-19:00
 *   Sun 11am-5pm
 *   Tue closed
 * Later lines win for the same day. Days not listed are closed.
 */
export function parseHours(text: string): HoursResult {
  const week: WeekHours = [[], [], [], [], [], [], []];
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return { ok: false, error: 'Add the store hours, like "Mon-Sat 10:00-19:00".' };
  for (const line of lines) {
    const m = /^([A-Za-z]{3,9}(?:\s*[-–]\s*[A-Za-z]{3,9})?(?:\s*,\s*[A-Za-z]{3,9}(?:\s*[-–]\s*[A-Za-z]{3,9})?)*)\s*:?\s+(.+)$/.exec(line);
    if (!m) return { ok: false, error: `Couldn't read "${line}". Write it like "Mon-Sat 10:00-19:00" or "Sun closed".` };
    const days: number[] = [];
    for (const part of m[1].split(',')) {
      const [a, b] = part.split(/[-–]/).map((s) => s.trim());
      const from = dayIndex(a);
      const to = b ? dayIndex(b) : from;
      if (from < 0 || to < 0) return { ok: false, error: `"${part.trim()}" isn't a day. Use Mon, Tue, Wed, Thu, Fri, Sat, Sun.` };
      for (let d = from; ; d = (d + 1) % 7) {
        days.push(d);
        if (d === to) break;
      }
    }
    const spec = m[2].trim();
    let ranges: { open: number; close: number }[] = [];
    if (!/^closed$/i.test(spec)) {
      for (const r of spec.split(',')) {
        const [o, c] = r.split(/\s*[-–]\s*|\s+to\s+/i);
        const open = o ? parseTime(o) : null;
        const close = c ? parseTime(c) : null;
        if (open === null || close === null) return { ok: false, error: `Couldn't read the times in "${line}". Use 10:00-19:00 or 10am-7pm.` };
        if (close <= open) return { ok: false, error: `In "${line}" the closing time has to be after the opening time.` };
        ranges.push({ open, close });
      }
      ranges = ranges.sort((x, y) => x.open - y.open);
    }
    for (const d of days) week[d] = ranges;
  }
  return { ok: true, week };
}

/** The weekday (0 = Sunday) and minutes after midnight in the store's time zone. */
export function localTime(ms: number, timeZone: string): { day: number; minutes: number } {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(ms));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return { day: Math.max(0, WEEKDAYS.indexOf(get('weekday') as (typeof WEEKDAYS)[number])), minutes: Number(get('hour')) * 60 + Number(get('minute')) };
}

export function isOpenAt(ms: number, week: WeekHours, timeZone: string): boolean {
  const { day, minutes } = localTime(ms, timeZone);
  return week[day].some((r) => minutes >= r.open && minutes < r.close);
}

function clock(min: number): string {
  const h = Math.floor(min / 60) % 24;
  const m = min % 60;
  return `${h % 12 === 0 ? 12 : h % 12}${m ? `:${String(m).padStart(2, '0')}` : ''}${h < 12 ? 'am' : 'pm'}`;
}

/** When the store opens next, in words: "today at 10am", "tomorrow at 10am", "Monday at 11am". */
export function nextOpening(ms: number, week: WeekHours, timeZone: string): string | null {
  const { day, minutes } = localTime(ms, timeZone);
  for (let i = 0; i < 8; i++) {
    const d = (day + i) % 7;
    const r = week[d].find((x) => (i === 0 ? x.open > minutes : true));
    if (r) return `${i === 0 ? 'today' : i === 1 ? 'tomorrow' : DAY_NAMES[d]} at ${clock(r.open)}`;
  }
  return null;
}

/** "Mon–Sat 10am–7pm, Sun 11am–5pm" for the assistant and the screens. */
export function describeHours(week: WeekHours): string {
  const sig = week.map((rs) => (rs.length ? rs.map((r) => `${clock(r.open)}–${clock(r.close)}`).join(', ') : 'closed'));
  const out: string[] = [];
  // Start on Monday so "Mon–Sat" reads naturally.
  const order = [1, 2, 3, 4, 5, 6, 0];
  let i = 0;
  while (i < order.length) {
    let j = i;
    while (j + 1 < order.length && sig[order[j + 1]] === sig[order[i]]) j++;
    const label = i === j ? WEEKDAYS[order[i]] : `${WEEKDAYS[order[i]]}–${WEEKDAYS[order[j]]}`;
    out.push(`${label} ${sig[order[i]]}`);
    i = j + 1;
  }
  return out.join(', ');
}

// ---------------------------------------------------------------- keywords

// Carriers and Twilio treat these single-word replies as opting out, back in, or asking for help.
const STOP_WORDS = ['STOP', 'STOPALL', 'UNSUBSCRIBE', 'CANCEL', 'END', 'QUIT', 'REVOKE', 'OPTOUT'];
const START_WORDS = ['START', 'UNSTOP', 'YES'];
const HELP_WORDS = ['HELP', 'INFO'];

export type Keyword = 'stop' | 'start' | 'help';

/** The keyword a whole message is, if it's only that word (punctuation and case don't matter). */
export function messageKeyword(body: string): Keyword | null {
  const w = body.trim().replace(/[^A-Za-z]/g, '').toUpperCase();
  if (!w || w.length > 12) return null;
  if (STOP_WORDS.includes(w)) return 'stop';
  if (START_WORDS.includes(w)) return 'start';
  if (HELP_WORDS.includes(w)) return 'help';
  return null;
}

// ---------------------------------------------------------------- templates

/** Fills {name} style blanks. Unknown blanks are left out. */
export function fillTemplate(template: string, vars: Record<string, string | number | null | undefined>): string {
  return template
    .replace(/\{(\w+)\}/g, (_, k: string) => (vars[k] === null || vars[k] === undefined ? '' : String(vars[k])))
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\s+([.,!?])/g, '$1')
    .trim();
}

/** Characters outside the basic SMS alphabet make every part shorter (70 instead of 160). */
export function smsParts(body: string): number {
  if (!body) return 0;
  // eslint-disable-next-line no-control-regex
  const gsm = /^[\x0A\x0D\x20-\x7E£¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ¡ÄÖÑÜ§¿äöñüà€]*$/.test(body);
  const per = gsm ? 160 : 70;
  const multi = gsm ? 153 : 67;
  // Twilio counts UTF-16 units for non-basic texts, so an emoji takes two.
  const len = gsm ? [...body].length : body.length;
  return len <= per ? 1 : Math.ceil(len / multi);
}

/** 10 digits → +1XXXXXXXXXX (what Twilio wants). */
export function toE164(digits: string): string {
  const d = digits.replace(/\D/g, '');
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith('1')) return `+${d}`;
  return d.startsWith('+') ? d : `+${d}`;
}
